import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  Loader2,
  Settings,
  VideoIcon,
} from 'lucide-react'
import Sidebar from '../components/layout/Sidebar'
import BuilderStepInlineError from '../components/meetingBuilder/BuilderStepInlineError'
import { builderInputClassName } from '../components/meetingBuilder/builderFieldStyles'
import MeetingSetupOverlay from '../components/common/MeetingSetupOverlay'
import { useToast } from '../components/common/Toast'
import { useAuth } from '../context/AuthContext'
import { useZoomOAuth } from '../context/ZoomOAuthContext'
import { dashboardAPI, meetingsAPI, zoomAPI } from '../services/api'
import {
  QUICK_START_DEFAULT_TITLE,
  QUICK_START_PROGRESS_STEPS,
  QUICK_START_STAGE,
  createInitialQuickStartFlowState,
  createQuickStartMeeting,
  getQuickStartAgendaItemValidationError,
  getQuickStartOrganizerTimezone,
  getQuickStartProgressIndex,
  quickStartFlowReducer,
} from '../utils/quickStartFlow'
import { getMeetingLiveInsightsPath } from '../utils/meetingRoutes'

const TEAL = '#16c1b2'
const TEAL_GRADIENT = 'linear-gradient(135deg, #16c1b2 0%, #0b6fa4 100%)'

function QuickStartTopBar() {
  return (
    <div className="flex items-center justify-between px-8 py-5 bg-white border-b border-gray-100 relative z-10">
      <Link to="/" className="flex items-center gap-3">
        <div className="w-16 h-14 flex items-center justify-center flex-shrink-0">
          <img src="/rithmo-mark.png" alt="Rithmo logo" className="w-full h-full object-contain" />
        </div>
        <span className="text-3xl font-bold text-gray-900">Rithmo</span>
      </Link>
      <Link
        to="/settings?standalone=true"
        className="p-2.5 rounded-xl hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
        aria-label="Settings"
      >
        <Settings className="w-6 h-6" />
      </Link>
    </div>
  )
}

function StepShell({ eyebrow, title, description, children, headerClassName = 'max-w-2xl' }) {
  return (
    <motion.div
      key={title}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="min-h-[360px] flex flex-col justify-center"
    >
      <div className={headerClassName}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: TEAL }}>{eyebrow}</p>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{title}</h1>
        {description ? (
          <p className="mt-3 text-base text-gray-500 sm:text-lg">{description}</p>
        ) : null}
      </div>
      <div className="mt-10">{children}</div>
    </motion.div>
  )
}

function DurationChoice({ minutes, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(minutes)}
      className="group flex w-full items-center justify-center rounded-3xl border border-gray-200 bg-white px-6 py-8 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#16c1b2]/40 hover:shadow-lg"
    >
      <span className="text-3xl font-bold text-gray-900">{minutes} minutes</span>
    </button>
  )
}

function QuickStartPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const standalone = searchParams.get('standalone') === 'true'
  const devBypassZoom = import.meta.env.DEV && searchParams.get('devBypassZoom') === 'true'
  const backTo = standalone ? '/home' : '/dashboard'
  const toast = useToast()
  const { user } = useAuth()
  const { isConnected: zoomConnected, connectZoomUrl } = useZoomOAuth()
  const effectiveZoomConnected = zoomConnected || devBypassZoom
  const [flowState, dispatch] = useReducer(
    quickStartFlowReducer,
    undefined,
    createInitialQuickStartFlowState
  )
  const [readinessLoading, setReadinessLoading] = useState(true)
  const [activeMeeting, setActiveMeeting] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [agendaItemFieldError, setAgendaItemFieldError] = useState(null)
  const createInFlightRef = useRef(false)
  const titleInputRef = useRef(null)
  const agendaInputRef = useRef(null)
  const pendingJoinWindowRef = useRef(null)

  const closePendingJoinWindow = useCallback(() => {
    const pendingWindow = pendingJoinWindowRef.current
    pendingJoinWindowRef.current = null
    if (pendingWindow && !pendingWindow.closed) {
      pendingWindow.close()
    }
  }, [])

  const takePendingJoinWindow = useCallback(() => {
    const pendingWindow = pendingJoinWindowRef.current
    pendingJoinWindowRef.current = null
    return pendingWindow && !pendingWindow.closed ? pendingWindow : null
  }, [])

  const reserveJoinWindow = useCallback(() => {
    if (pendingJoinWindowRef.current && !pendingJoinWindowRef.current.closed) {
      return pendingJoinWindowRef.current
    }

    const pendingWindow = window.open('', '_blank')
    if (pendingWindow?.document) {
      try {
        pendingWindow.document.title = 'Starting Rithmo meeting...'
        pendingWindow.document.body.innerHTML =
          '<div style="font-family: system-ui, sans-serif; padding: 24px; color: #111827;">Starting your Zoom meeting...</div>'
      } catch {
        // Cross-window document writes are best-effort only.
      }
    }
    pendingJoinWindowRef.current = pendingWindow
    return pendingWindow
  }, [])

  useEffect(() => {
    let isActive = true
    setReadinessLoading(true)
    dashboardAPI.getActiveMeeting()
      .then((response) => {
        if (!isActive) return
        const nextActiveMeeting = response?.data?.activeMeeting ?? response?.activeMeeting ?? null
        setActiveMeeting(nextActiveMeeting)
      })
      .catch(() => {
        if (isActive) setActiveMeeting(null)
      })
      .finally(() => {
        if (isActive) setReadinessLoading(false)
      })
    return () => {
      isActive = false
    }
  }, [])

  useEffect(() => () => {
    closePendingJoinWindow()
  }, [closePendingJoinWindow])

  useEffect(() => {
    if (flowState.stage === QUICK_START_STAGE.TITLE) {
      titleInputRef.current?.focus()
    }
    if (flowState.stage === QUICK_START_STAGE.AGENDA_ITEM) {
      agendaInputRef.current?.focus()
    }
  }, [flowState.stage, flowState.agendaItemIndex])

  useEffect(() => {
    setAgendaItemFieldError(null)
  }, [flowState.stage, flowState.agendaItemIndex])

  useEffect(() => {
    if (
      flowState.stage !== QUICK_START_STAGE.CREATING ||
      createInFlightRef.current ||
      readinessLoading ||
      activeMeeting ||
      !effectiveZoomConnected
    ) {
      return
    }

    let cancelled = false
    createInFlightRef.current = true
    setIsCreating(true)

    const run = async () => {
      let reservedMeetingId = null
      let startCompleted = false
      try {
        const createdMeeting = await createQuickStartMeeting({
          meetingsApi: meetingsAPI,
          durationMinutes: flowState.durationMinutes,
          title: flowState.title,
          agendaItems: flowState.agendaItems,
          organizerTimezone: getQuickStartOrganizerTimezone(user),
        })

        if (cancelled) return

        reservedMeetingId = createdMeeting.meetingId

        await meetingsAPI.updateStatus(createdMeeting.meetingId, {
          status: 'starting',
        })

        const callStart = (force = false) =>
          zoomAPI.startExistingMeetingForUser({
            meetingId: createdMeeting.meetingId,
            password: createdMeeting.password ?? undefined,
            force,
          })

        try {
          await callStart(false)
        } catch (startError) {
          if (startError?.status === 409 && startError?.code === 'CONCURRENT_ZOOM_CALL') {
            const confirmed = window.confirm(
              'You are currently in another Zoom meeting. Starting this meeting will end the other call. Continue?'
            )
            if (!confirmed) {
              throw new Error('Please leave your other Zoom meeting and try again.')
            }
            await callStart(true)
          } else if (startError?.status === 409 && startError?.code === 'CONCURRENT_ZOOM_CALL_DETECTED') {
            throw new Error('Please leave your other Zoom meeting and try again.')
          } else {
            throw startError
          }
        }

        startCompleted = true

        const pendingWindow = takePendingJoinWindow()
        if (pendingWindow) {
          pendingWindow.location = createdMeeting.joinUrl
        } else {
          window.open(createdMeeting.joinUrl, '_blank')
        }

        toast.success('Meeting Created!', 'Your Zoom-backed meeting is ready.')
        navigate(`/meeting/${createdMeeting.meetingId}`, {
          replace: true,
          state: {
            joinUrl: createdMeeting.joinUrl,
            password: createdMeeting.password,
            createdWithUserZoom: createdMeeting.createdWithUserZoom,
            cppBotJoined: true,
            addBotAfterYouJoin: false,
          },
        })
      } catch (error) {
        if (cancelled) return

        closePendingJoinWindow()

        if (reservedMeetingId && !startCompleted) {
          try {
            await meetingsAPI.updateStatus(reservedMeetingId, { status: 'created' })
          } catch {
            // Best-effort revert only.
          }
        }

        const message = error?.message || 'Failed to create the meeting. Please try again.'
        dispatch({ type: 'create-failed', message })

        if (
          message.includes('zoom_reauth_required') ||
          message.includes('Zoom connection has expired')
        ) {
          toast.error('Zoom Disconnected', 'Your Zoom connection has expired. Reconnecting...')
          if (connectZoomUrl) {
            window.setTimeout(() => {
              window.location.href = connectZoomUrl
            }, 1500)
          }
        } else {
          toast.error('Creation Failed', message)
        }
      } finally {
        createInFlightRef.current = false
        if (!cancelled) {
          setIsCreating(false)
        }
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [
    activeMeeting,
    connectZoomUrl,
    flowState.agendaItems,
    flowState.durationMinutes,
    flowState.stage,
    flowState.title,
    navigate,
    readinessLoading,
    toast,
    closePendingJoinWindow,
    takePendingJoinWindow,
    user,
    zoomConnected,
    effectiveZoomConnected,
  ])

  const progressIndex = useMemo(
    () => getQuickStartProgressIndex(flowState.stage),
    [flowState.stage]
  )

  const currentAgendaValue = flowState.agendaItems[flowState.agendaItemIndex] ?? ''
  const agendaPrompt =
    flowState.agendaItemIndex === 0
      ? 'What is the first agenda item?'
      : `What is agenda item ${flowState.agendaItemIndex + 1}?`
  const canGoBack = flowState.history.length > 0 && flowState.stage !== QUICK_START_STAGE.CREATING

  const handleTitleSubmit = (event) => {
    event.preventDefault()
    dispatch({ type: 'continue-title' })
  }

  const handleAgendaSubmit = (event) => {
    event.preventDefault()
    const validationError = getQuickStartAgendaItemValidationError(
      currentAgendaValue,
      flowState.agendaItemIndex
    )
    if (validationError) {
      setAgendaItemFieldError(validationError)
      agendaInputRef.current?.focus({ preventScroll: true })
      return
    }
    setAgendaItemFieldError(null)
    dispatch({ type: 'continue-agenda-item' })
  }

  const showFlow = !readinessLoading && effectiveZoomConnected && !activeMeeting

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top,rgba(22,193,178,0.12),transparent_62%)]" />
      <div className="pointer-events-none absolute right-0 top-20 h-64 w-64 rounded-full bg-[#e4faf8] blur-3xl opacity-60" />
      {!standalone && <Sidebar />}
      {standalone && <QuickStartTopBar />}

      <MeetingSetupOverlay
        visible={isCreating}
        title="Setting Up Rithmo"
        subtitle="Creating your meeting..."
      />

      <main className={`${standalone ? '' : 'lg:ml-[280px]'} relative overflow-hidden p-4 ${standalone ? 'pt-8' : 'pt-20'} lg:p-8 lg:pt-8`}>

        <div className="relative mx-auto max-w-5xl">
          <div className="mb-6 flex items-center justify-between gap-3">
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              {standalone ? 'Back' : 'Dashboard'}
            </Link>
          </div>

          {readinessLoading ? (
            <div className="glass rounded-[32px] p-8 sm:p-10">
              <div className="flex min-h-[360px] flex-col items-center justify-center text-center">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: TEAL }} />
                <h1 className="mt-6 text-2xl font-bold text-gray-900">Checking your meeting setup</h1>
                <p className="mt-2 max-w-md text-gray-500">
                  One moment while Quick Start makes sure you can create a Zoom-backed meeting.
                </p>
              </div>
            </div>
          ) : !effectiveZoomConnected ? (
            <div className="glass rounded-[32px] p-8 sm:p-10">
              <div className="flex min-h-[360px] flex-col justify-center">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#16c1b2]">
                  Instant Meeting
                </p>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  Connect Zoom First
                </h1>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <a
                    href={connectZoomUrl || '#'}
                    className="btn-primary inline-flex items-center gap-2"
                    onClick={(event) => {
                      if (!connectZoomUrl) {
                        event.preventDefault()
                      }
                    }}
                  >
                    <VideoIcon className="w-5 h-5" />
                    Connect to Zoom
                  </a>
                </div>
              </div>
            </div>
          ) : activeMeeting ? (
            <div className="glass rounded-[32px] p-8 sm:p-10">
              <div className="flex min-h-[360px] flex-col justify-center">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#16c1b2]">
                  Quick Start
                </p>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  Finish your live meeting first
                </h1>
                <p className="mt-3 max-w-2xl text-base text-gray-500 sm:text-lg">
                  Rithmo already sees one of your meetings as live or starting. Quick Start stays aligned
                  with the dashboard guardrail that blocks opening another meeting at the same time.
                </p>
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                  <div>
                    <p className="font-semibold text-amber-900">Meeting in Progress</p>
                    <p>{activeMeeting.title || 'Untitled meeting'}</p>
                  </div>
                </div>
                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <Link
                    to={getMeetingLiveInsightsPath(activeMeeting)}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    View Live Meeting Insights
                  </Link>
                  <Link to={backTo} className="btn-secondary inline-flex items-center gap-2">
                    {standalone ? 'Back' : 'Back to dashboard'}
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass rounded-[32px] p-6 sm:p-8 lg:p-10">
              <div className="flex items-center justify-end border-b border-gray-100 pb-5">
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'back' })}
                  disabled={!canGoBack}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                    canGoBack
                      ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              </div>

              <div className="mt-6">
                <div className="grid grid-cols-4 gap-2">
                  {QUICK_START_PROGRESS_STEPS.map((stepLabel, index) => (
                    <div
                      key={stepLabel}
                      className={`h-1.5 rounded-full ${
                        index <= progressIndex ? 'bg-[#16c1b2]' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {flowState.error ? (
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                  <span>{flowState.error}</span>
                </div>
              ) : null}

              <AnimatePresence mode="wait">
                {showFlow && flowState.stage === QUICK_START_STAGE.DURATION ? (
                  <StepShell
                    eyebrow="Step 1"
                    title="How long is the meeting?"
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <DurationChoice
                        minutes={30}
                        onSelect={(durationMinutes) => dispatch({ type: 'select-duration', durationMinutes })}
                      />
                      <DurationChoice
                        minutes={60}
                        onSelect={(durationMinutes) => dispatch({ type: 'select-duration', durationMinutes })}
                      />
                    </div>
                  </StepShell>
                ) : null}

                {showFlow && flowState.stage === QUICK_START_STAGE.TITLE ? (
                  <StepShell
                    eyebrow="Step 2"
                    headerClassName="max-w-5xl"
                    title="Do you want to give the meeting a title?"
                    description={`Leave it blank and Quick Start will use “${QUICK_START_DEFAULT_TITLE}.”`}
                  >
                    <form onSubmit={handleTitleSubmit} className="max-w-2xl">
                      <label className="sr-only" htmlFor="quick-start-title">Meeting title</label>
                      <input
                        id="quick-start-title"
                        ref={titleInputRef}
                        type="text"
                        value={flowState.title}
                        onChange={(event) => dispatch({ type: 'update-title', value: event.target.value })}
                        placeholder="Weekly team sync"
                        className="w-full rounded-3xl border border-gray-200 bg-white px-5 py-4 text-lg text-gray-900 shadow-sm outline-none transition focus:border-[#16c1b2]/50 focus:ring-4 focus:ring-[#16c1b2]/10"
                      />
                      <div className="mt-5">
                        <button type="submit" className="btn-primary inline-flex items-center gap-2">
                          Continue
                        </button>
                      </div>
                    </form>
                  </StepShell>
                ) : null}

                {showFlow && flowState.stage === QUICK_START_STAGE.AGENDA_ITEM ? (
                  <StepShell
                    eyebrow="Step 3"
                    title={agendaPrompt}
                    description="Keep it short. You can add more items one at a time."
                  >
                    <form onSubmit={handleAgendaSubmit} className="max-w-2xl">
                      <label className="sr-only" htmlFor="quick-start-agenda-item">Agenda item</label>
                      <input
                        id="quick-start-agenda-item"
                        ref={agendaInputRef}
                        type="text"
                        value={currentAgendaValue}
                        onChange={(event) => {
                          dispatch({ type: 'update-agenda-item', value: event.target.value })
                          if (agendaItemFieldError) setAgendaItemFieldError(null)
                        }}
                        placeholder={
                          agendaItemFieldError
                            ? 'Add a title to continue'
                            : flowState.agendaItemIndex === 0
                              ? 'Budget review'
                              : 'Risks and blockers'
                        }
                        aria-invalid={Boolean(agendaItemFieldError)}
                        className={builderInputClassName(Boolean(agendaItemFieldError), 'large')}
                      />
                      <BuilderStepInlineError message={agendaItemFieldError} />
                      <div className="mt-5 flex flex-col items-start gap-3">
                        <button type="submit" className="btn-primary inline-flex items-center gap-2">
                          Continue
                        </button>
                        {flowState.agendaItemIndex > 0 ? (
                          <button
                            type="button"
                            className="text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
                            onClick={() => dispatch({ type: 'finish-adding-agenda-items' })}
                          >
                            I&apos;m done adding items
                          </button>
                        ) : null}
                      </div>
                    </form>
                  </StepShell>
                ) : null}

                {showFlow && flowState.stage === QUICK_START_STAGE.ADD_ANOTHER ? (
                  <StepShell
                    eyebrow="Step 4"
                    title="Do you want to add another agenda item?"
                    description="You can stop here and create the meeting right away."
                  >
                    <div className="max-w-2xl">
                      <div className="mb-6 flex flex-wrap gap-2">
                        {flowState.agendaItems
                          .map((item) => String(item ?? '').trim())
                          .filter(Boolean)
                          .map((item, index) => (
                            <span
                              key={`${item}-${index}`}
                              className="inline-flex items-center rounded-full bg-[#e4faf8] px-3 py-1 text-sm font-medium text-[#0d8f84]"
                            >
                              {item}
                            </span>
                          ))}
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <button
                          type="button"
                          className="flex items-center justify-center rounded-3xl border border-gray-200 bg-white px-5 py-8 text-center shadow-sm transition hover:border-[#16c1b2]/40 hover:shadow-lg"
                          onClick={() => dispatch({ type: 'choose-add-another', addAnother: true })}
                        >
                          <span className="text-lg font-semibold text-gray-900">Yes, Add Another</span>
                        </button>
                        <button
                          type="button"
                          className="flex items-center justify-center rounded-3xl border border-[#16c1b2]/40 bg-[#e4faf8] px-5 py-8 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
                          onClick={() => {
                            reserveJoinWindow()
                            dispatch({ type: 'choose-add-another', addAnother: false })
                          }}
                        >
                          <span className="text-lg font-semibold text-gray-900">No. Start Meeting Now.</span>
                        </button>
                      </div>
                    </div>
                  </StepShell>
                ) : null}

                {showFlow && flowState.stage === QUICK_START_STAGE.CREATING ? (
                  <StepShell
                    eyebrow="Creating"
                    title="Creating your meeting"
                    description="Quick Start is using the same Zoom-backed path as the full builder."
                  >
                    <div className="flex max-w-xl items-center gap-3 rounded-3xl border border-[#16c1b2]/20 bg-[#e4faf8] px-5 py-4 text-[#0d8f84]">
                      <Loader2 className="h-5 w-5 animate-spin" style={{ color: TEAL }} />
                      <span className="font-medium">Your Zoom meeting is being created now.</span>
                    </div>
                  </StepShell>
                ) : null}
              </AnimatePresence>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default QuickStartPage
